(() => {
  'use strict';
  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const get = id => root.querySelector('#' + id);
    const form = get('checkout-form'), submit = get('checkout-submit'), error = get('checkout-error');
    const base = root.dataset.root || '/';
    let cart = null, busy = false;
    const money = cents => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cents / 100);
    const show = (id, visible) => get(id)?.classList.toggle('hidden', !visible);
    const fail = message => { error.textContent = message; error.hidden = !message; };
    function setBusy(value) {
      busy = value;
      form.setAttribute('aria-busy', String(value));
      submit.disabled = value || !cart?.items?.length || !(cart.total_price > 0);
      root.querySelectorAll('[data-cart-action]').forEach(button => { button.disabled = value; });
      submit.textContent = value ? 'Even geduld...' : cart?.total_price > 0 ? 'Doorgaan naar betalen — ' + money(cart.total_price) : 'Doorgaan naar betalen';
    }
    async function request(url, options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal, cache: 'no-store' });
        const data = await response.json();
        if (!response.ok) throw new Error('Aanvraag mislukt');
        return data;
      } finally { clearTimeout(timeout); }
    }
    async function fetchCart() {
      const result = await request(base + 'cart.js', { credentials: 'same-origin' });
      if (!Array.isArray(result.items) || !Number.isSafeInteger(result.total_price) || result.total_price < 0) throw new Error('Ongeldige winkelwagen');
      if (result.currency !== 'EUR') throw new Error('Deze betaalpagina ondersteunt alleen euro’s.');
      return result;
    }
    function text(tag, className, value) {
      const el = document.createElement(tag); el.className = className; el.textContent = value; return el;
    }
    function renderCart() {
      const items = cart.items;
      get('order-items').replaceChildren();
      get('order-empty').textContent = 'Je winkelwagen is leeg. Voeg eerst producten toe aan je winkelwagen.';
      show('order-empty', !items.length);
      ['order-items', 'order-totals', 'order-divider'].forEach(id => show(id, !!items.length));
      items.forEach(item => {
        const row = text('div', 'cart-item', '');
        if (item.image || item.featured_image?.url) {
          const image = document.createElement('img');
          const url = new URL(item.image || item.featured_image.url, location.origin);
          if (url.protocol === 'https:' || url.origin === location.origin) image.src = url.href;
          image.alt = item.product_title || item.title; image.width = 72; image.height = 72; image.className = 'cart-item-image'; row.append(image);
        }
        const info = text('div', 'cart-item-info', '');
        info.append(text('div', 'cart-item-title', item.product_title || item.title));
        if (item.variant_title && item.variant_title !== 'Default Title') info.append(text('div', 'cart-item-variant', item.variant_title));
        const controls = text('div', 'cart-qty-control', '');
        function button(label, quantity, action) {
          const btn = text('button', 'cart-qty-button', label); btn.type = 'button';
          btn.dataset.cartKey = item.key; btn.dataset.quantity = String(quantity); btn.dataset.cartAction = action;
          btn.setAttribute('aria-label', (action === 'increase' ? 'Aantal verhogen: ' : 'Aantal verlagen: ') + (item.product_title || item.title));
          return btn;
        }
        controls.append(button('−', item.quantity - 1, 'decrease'), text('span', 'cart-qty-value', item.quantity), button('+', item.quantity + 1, 'increase'));
        info.append(controls); row.append(info);
        const prices = text('div', 'cart-item-price', '');
        if (item.original_line_price > item.final_line_price) prices.append(text('del', '', money(item.original_line_price)));
        prices.append(text('strong', '', money(item.final_line_price ?? item.line_price)));
        row.append(prices); get('order-items').append(row);
      });
      const subtotal = cart.original_total_price ?? cart.total_price;
      const discount = Math.max(0, subtotal - cart.total_price);
      get('order-subtotal').textContent = money(subtotal);
      get('order-discount').textContent = '− ' + money(discount);
      show('order-discount-row', discount > 0);
      get('order-total').textContent = money(cart.total_price);
      document.querySelectorAll('.header__cart-count').forEach(el => { el.textContent = cart.item_count; });
    }
    async function load() {
      setBusy(true); fail('');
      try { cart = await fetchCart(); renderCart(); }
      catch (e) { cart = null; fail(e.message.includes('euro') ? e.message : 'Je winkelwagen kon niet worden geladen. Vernieuw de pagina om het opnieuw te proberen.'); }
      finally { setBusy(false); }
    }
    get('order-items').addEventListener('click', async event => {
      const button = event.target.closest('[data-cart-action]');
      if (!button || busy) return;
      setBusy(true); fail('');
      try {
        await request(base + 'cart/change.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: button.dataset.cartKey, quantity: Number(button.dataset.quantity) }) });
        cart = await fetchCart(); renderCart();
      } catch (_) { cart = null; fail('Het aantal kon niet worden bijgewerkt. Vernieuw de pagina voordat je verdergaat.'); }
      finally { setBusy(false); }
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy || !form.reportValidity()) return;
      if (root.dataset.designMode === 'true') { fail('Dit is een voorbeeld in de thema-editor. Er wordt geen betaling gestart.'); return; }
      setBusy(true); fail('');
      let paymentRequested = false;
      let navigating = false;
      const shownTotal = cart?.total_price;
      try {
        const fresh = await fetchCart();
        cart = fresh; renderCart();
        if (!cart.items.length || cart.total_price <= 0) throw new Error('Je winkelwagen is leeg.');
        if (shownTotal !== cart.total_price) { fail('Het totaalbedrag is gewijzigd. Controleer je bestelling en klik opnieuw op betalen.'); return; }
        const value = id => get(id).value.trim();
        const products = cart.items.map(item => {
          const line = item.final_line_price ?? item.line_price;
          const unit = item.final_price ?? Math.round(line / item.quantity);
          return {
            title: item.product_title || item.title, name: item.product_title || item.title, product_title: item.product_title || item.title,
            variantTitle: item.variant_title || '', variant_title: item.variant_title || '', quantity: item.quantity,
            price: unit, originalPrice: item.original_price ?? item.price, original_price: item.original_price ?? item.price,
            linePrice: line, line_price: line, originalLinePrice: item.original_line_price ?? line,
            discountAmount: Math.max(0, (item.original_line_price ?? line) - line),
            discountPercentage: item.original_line_price > 0 ? Math.round((1 - line / item.original_line_price) * 100) : 0,
            finalPrice: unit, final_price: unit, productId: item.product_id, product_id: item.product_id,
            variantId: item.variant_id, variant_id: item.variant_id, sku: item.sku || '', key: item.key,
            image: item.image || item.featured_image?.url || ''
          };
        });
        const payload = {
          amount: (cart.total_price / 100).toFixed(2),
          description: products.map(item => `${item.quantity}x ${item.title}`).join(', '), method: 'ideal',
          email: value('email'), phone: value('phone'),
          customer: { email: value('email'), phone: value('phone'), firstName: value('first-name'), lastName: value('last-name') },
          shippingAddress: { firstName: value('first-name'), lastName: value('last-name'), street: value('street'), houseNumber: value('house-number'), postcode: value('postcode'), city: value('city'), country: value('country'), phone: value('phone') },
          products, lineItems: products,
          cart: { itemCount: cart.item_count, totalPrice: cart.total_price, itemsSubtotalPrice: cart.original_total_price ?? cart.total_price, totalDiscount: cart.total_discount || 0 }
        };
        show('loading-overlay', true);
        paymentRequested = true;
        const data = await request(root.dataset.paymentEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const url = new URL(data.payment_url);
        if (url.protocol !== 'https:' || url.username || url.password) throw new Error('Ongeldige betaallink');
        navigating = true;
        window.location.assign(url.href);
      } catch (_) {
        fail(paymentRequested ? 'We konden de betaalomgeving niet openen. Controleer of er al een betaling is gestart voordat je opnieuw probeert, of neem contact op met de klantenservice.' : 'Je winkelwagen kon niet worden bevestigd. Vernieuw de pagina en controleer je bestelling.');
      } finally { if (!navigating) { show('loading-overlay', false); setBusy(false); } }
    });
    load();
    window.addEventListener('pageshow', event => { if (event.persisted) load(); });
  }
  document.querySelectorAll('[data-mw-checkout]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-mw-checkout]').forEach(init));
})();
